import React, { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountTree as AccountTreeIcon,
  ArrowBack as ArrowBackIcon,
  GridView as GridViewIcon,
  OpenInNew as OpenInNewIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import apiService, {
  type PodcastTaxonomyProgramRow,
  type PodcastTaxonomyTopicRow,
} from "../services/api";
import {
  contentTaxonomyTopicsPath,
  podcastContentAnalysisPath,
} from "../constants/reviewRoutePaths";
import { useFilterStore } from "../stores/filterStore";
import {
  buildMonthOptions,
  buildWeekOptions,
  formatDayRangeLabel,
} from "../utils/reviewDateFilterOptions";
import TopicNetworkGraph from "./taxonomy/TopicNetworkGraph";
import TopicTreemap from "./taxonomy/TopicTreemap";
import {
  buildTopicHierarchyFromAggregate,
  buildTopicColorMap,
  hierarchyHasSubtopics,
  type DominanceLevel,
  type TopicMetric,
} from "./taxonomy/taxonomyHierarchy";
import ProgramTreemapGrid from "./taxonomy/ProgramTreemapGrid";
import ProgramTopicNetworkGraph from "./taxonomy/ProgramTopicNetworkGraph";
import SubtopicChunksPanel from "./taxonomy/SubtopicChunksPanel";
import type { TreemapSubtopicSelection } from "./taxonomy/subtopicChunkMatch";

type PageTab = "overview" | "by-program";
type DominanceView = "treemap" | "network";
type ScopeView = "aggregate" | "compare";

function formatReviewPeriodLabel(
  mode: "week" | "month" | "day",
  selectedWeek: string,
): string {
  if (selectedWeek === "all") return "All time";
  if (mode === "day") return formatDayRangeLabel(selectedWeek);
  const options = mode === "week" ? buildWeekOptions() : buildMonthOptions();
  return options.find((o) => o.value === selectedWeek)?.label ?? selectedWeek;
}

function formatViews(n: number): string {
  return n.toLocaleString("en-US");
}

function formatRate(value: number | null | undefined): string {
  if (value == null) return "—";
  return value.toFixed(2);
}

function formatShare(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function programMentionsPer1k(program: PodcastTaxonomyProgramRow): number | null {
  const totalMentions = program.topics.reduce(
    (sum: number, t: PodcastTaxonomyTopicRow) => sum + t.mentions,
    0,
  );
  if (program.metrics.totalViews <= 0) return null;
  return Math.round((totalMentions / (program.metrics.totalViews / 1000)) * 100) / 100;
}

function programLabel(
  programId: string | null,
  programs: Array<{ _id: string; name: string }> | undefined,
): string {
  if (!programId || programId === "all") return "All programs";
  if (programId === "unassigned") return "Unassigned";
  return programs?.find((p) => p._id === programId)?.name ?? programId;
}

function MetricsStrip({
  metrics,
}: {
  metrics: {
    episodeCount: number;
    episodesWithTaxonomy: number;
    totalViews: number;
    totalViewsDay7: number;
  };
}) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      <Chip label={`${metrics.episodeCount} episodes in scope`} />
      <Chip
        label={`${metrics.episodesWithTaxonomy} with taxonomy`}
        color={metrics.episodesWithTaxonomy > 0 ? "success" : "default"}
        variant="outlined"
      />
      <Chip label={`${formatViews(metrics.totalViews)} total views`} variant="outlined" />
      <Tooltip title="Sum of day-7 views when available, otherwise latest metric views">
        <Chip
          label={`${formatViews(metrics.totalViewsDay7)} day-7 views`}
          variant="outlined"
        />
      </Tooltip>
    </Stack>
  );
}

function TopTopicsChips({ topics, limit = 3 }: { topics: PodcastTaxonomyTopicRow[]; limit?: number }) {
  if (topics.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {topics.slice(0, limit).map((topic) => (
        <Tooltip
          key={topic.slug}
          title={`${topic.mentions} mentions · ${formatRate(topic.mentionsPer1kViews)}/1k views`}
        >
          <Chip size="small" label={`${topic.display} (${topic.mentions})`} />
        </Tooltip>
      ))}
    </Stack>
  );
}

function TopicTable({ topics }: { topics: PodcastTaxonomyTopicRow[] }) {
  if (topics.length === 0) return null;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Topic</TableCell>
            <TableCell align="right">Mentions</TableCell>
            <TableCell align="right">Episodes</TableCell>
            <TableCell align="right">
              <Tooltip title="Mentions weighted by episode YouTube views in period">
                <span>Weighted views</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Mentions per 1,000 views in scope">
                <span>Mentions / 1k views</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Share of total view-weighted mentions in scope">
                <span>View share</span>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {topics.map((topic) => (
            <TableRow key={topic.slug}>
              <TableCell>
                <Typography variant="body2">{topic.display}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {topic.slug}
                </Typography>
              </TableCell>
              <TableCell align="right">{topic.mentions}</TableCell>
              <TableCell align="right">{topic.episodeCount}</TableCell>
              <TableCell align="right">{formatViews(topic.weightedMentions)}</TableCell>
              <TableCell align="right">{formatRate(topic.mentionsPer1kViews)}</TableCell>
              <TableCell align="right">{formatShare(topic.viewShare)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TopEpisodesSection({ topics }: { topics: PodcastTaxonomyTopicRow[] }) {
  const episodes = useMemo(() => {
    const seen = new Set<string>();
    const rows: Array<{
      contentId: string;
      title: string;
      views: number;
      mentions: number;
      topicSlug: string;
      topicDisplay: string;
    }> = [];

    for (const topic of topics) {
      for (const ep of topic.topEpisodes ?? []) {
        const key = `${topic.slug}:${ep.contentId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({
          ...ep,
          topicSlug: topic.slug,
          topicDisplay: topic.display,
        });
      }
    }

    return rows.sort((a, b) => b.mentions - a.mentions || b.views - a.views).slice(0, 12);
  }, [topics]);

  if (episodes.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Top episodes driving topics
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Episode</TableCell>
            <TableCell>Topic</TableCell>
            <TableCell align="right">Mentions</TableCell>
            <TableCell align="right">Views</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {episodes.map((ep) => (
            <TableRow key={`${ep.topicSlug}-${ep.contentId}`}>
              <TableCell>{ep.title}</TableCell>
              <TableCell>{ep.topicDisplay}</TableCell>
              <TableCell align="right">{ep.mentions}</TableCell>
              <TableCell align="right">{formatViews(ep.views)}</TableCell>
              <TableCell align="right">
                <Button
                  component={RouterLink}
                  to={contentTaxonomyTopicsPath(ep.contentId)}
                  size="small"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                >
                  Topics
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

const PodcastTaxonomyTopics: React.FC = () => {
  const { podcastId } = useParams<{ podcastId: string }>();
  const {
    selectedWeek,
    reviewDateFilterMode,
    selectedProgram,
    setSelectedProgram,
  } = useFilterStore();

  const [pageTab, setPageTab] = useState<PageTab>("overview");
  const [dominanceView, setDominanceView] = useState<DominanceView>("treemap");
  const [dominanceLevel, setDominanceLevel] = useState<DominanceLevel>("topic");
  const [scopeView, setScopeView] = useState<ScopeView>("aggregate");
  const [metric, setMetric] = useState<TopicMetric>("mentions");
  const [expandedProgramId, setExpandedProgramId] = useState<string | null | undefined>(
    undefined,
  );
  const [subtopicSelection, setSubtopicSelection] = useState<TreemapSubtopicSelection | null>(
    null,
  );

  const weekParam = selectedWeek !== "all" ? selectedWeek : undefined;
  const programParam =
    selectedProgram !== "all" ? selectedProgram : undefined;

  const { data: programsData } = useQuery({
    queryKey: ["podcast-programs", podcastId],
    queryFn: () => apiService.getPodcastPrograms(podcastId!),
    enabled: !!podcastId,
  });

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: [
      "podcast-taxonomy-summary",
      podcastId,
      weekParam,
      programParam,
    ],
    queryFn: () =>
      apiService.getPodcastTaxonomySummary(podcastId!, {
        week: weekParam,
        programId: programParam,
      }),
    enabled: !!podcastId,
  });

  const { data: byProgram, isLoading: byProgramLoading } = useQuery({
    queryKey: ["podcast-taxonomy-by-program", podcastId, weekParam],
    queryFn: () =>
      apiService.getPodcastTaxonomyByProgram(podcastId!, { week: weekParam }),
    enabled:
      !!podcastId &&
      (pageTab === "by-program" || (pageTab === "overview" && scopeView === "compare")),
  });

  const colorBySlug = useMemo(
    () => (summary?.topics ? buildTopicColorMap(summary.topics, metric) : new Map()),
    [summary?.topics, metric],
  );

  const topicHierarchy = useMemo(
    () =>
      summary?.topics ? buildTopicHierarchyFromAggregate(summary.topics, metric) : [],
    [summary?.topics, metric],
  );

  const expandedProgram = useMemo(() => {
    if (expandedProgramId === undefined || !byProgram) return null;
    return (
      byProgram.programs.find((program) =>
        expandedProgramId === null
          ? program.programId === null
          : program.programId === expandedProgramId,
      ) ?? null
    );
  }, [byProgram, expandedProgramId]);

  const expandedProgramHierarchy = useMemo(
    () =>
      expandedProgram
        ? buildTopicHierarchyFromAggregate(expandedProgram.topics, metric)
        : [],
    [expandedProgram, metric],
  );

  const subtopicChunkProgramParam = useMemo(() => {
    if (scopeView === "compare" && expandedProgramId !== undefined) {
      return expandedProgramId === null ? "unassigned" : expandedProgramId;
    }
    return programParam;
  }, [scopeView, expandedProgramId, programParam]);

  const { data: subtopicChunksData, isLoading: subtopicChunksLoading } = useQuery({
    queryKey: [
      "podcast-taxonomy-subtopic-chunks",
      podcastId,
      weekParam,
      subtopicChunkProgramParam,
      subtopicSelection?.topicSlug,
      subtopicSelection?.subtopicSlug,
    ],
    queryFn: () =>
      apiService.getPodcastTaxonomySubtopicChunks(podcastId!, {
        topicSlug: subtopicSelection!.topicSlug,
        subtopicSlug: subtopicSelection!.subtopicSlug,
        week: weekParam,
        programId: subtopicChunkProgramParam,
      }),
    enabled: !!podcastId && !!subtopicSelection,
  });

  const periodLabel = formatReviewPeriodLabel(reviewDateFilterMode, selectedWeek);
  const backHref = podcastContentAnalysisPath(podcastId ?? "");

  const handleProgramRowClick = (program: PodcastTaxonomyProgramRow) => {
    setSelectedProgram(program.programId ?? "unassigned");
    setPageTab("overview");
  };

  if (summaryLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (summaryError || !summary) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load podcast taxonomy summary.</Alert>
      </Container>
    );
  }

  const hasTaxonomy = summary.metrics.episodesWithTaxonomy > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Button component={RouterLink} to={backHref} startIcon={<ArrowBackIcon />} size="small">
            Back to review
          </Button>
        </Box>

        <Box>
          <Typography variant="h5" gutterBottom>
            Topics explorer
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {summary.podcast.title}
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              <TuneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Filters from podcast review (read-only)
              </Typography>
              <Link component={RouterLink} to={backHref} variant="body2">
                Adjust on podcast detail
              </Link>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip label={periodLabel} size="small" />
              <Chip
                label={programLabel(selectedProgram, programsData?.programs)}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Paper>

        <MetricsStrip metrics={summary.metrics} />

        <Tabs
          value={pageTab}
          onChange={(_, value: PageTab) => setPageTab(value)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Overview" value="overview" />
          <Tab label="By program" value="by-program" />
        </Tabs>

        {!hasTaxonomy && (
          <Alert severity="info">
            No episodes with accepted taxonomy in this period. Run{" "}
            <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
              yarn taxonomy:candidates --podcast-id {podcastId}
              {weekParam ? ` --date-from … --date-to …` : ""}
            </Typography>{" "}
            for episodes in range.
          </Alert>
        )}

        {pageTab === "overview" && hasTaxonomy && (
          <Stack spacing={2}>
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={scopeView}
                onChange={(_, value: ScopeView | null) => {
                  if (value) {
                    setScopeView(value);
                    setExpandedProgramId(undefined);
                  }
                }}
              >
                <ToggleButton value="aggregate">All programs</ToggleButton>
                <ToggleButton value="compare">Compare programs</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={metric}
                onChange={(_, value: TopicMetric | null) => {
                  if (value) setMetric(value);
                }}
              >
                <ToggleButton value="mentions">Mentions</ToggleButton>
                <ToggleButton value="weightedMentions">View-weighted</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {scopeView === "aggregate" && dominanceView === "network" && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={dominanceLevel}
                onChange={(_, value: DominanceLevel | null) => {
                  if (value) setDominanceLevel(value);
                }}
              >
                <ToggleButton value="topic">Topics</ToggleButton>
                <ToggleButton value="subtopic" disabled={!hierarchyHasSubtopics(topicHierarchy)}>
                  Subtopics
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {(topicHierarchy.length > 0 || scopeView === "compare") && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="subtitle1">Topic dominance</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {scopeView === "compare"
                        ? dominanceView === "treemap"
                          ? expandedProgram
                            ? `Drill-down for ${expandedProgram.programName}. Click a topic to explore subtopics.`
                            : "One mini-treemap per program. Card size reflects program total; colors match across programs."
                          : "Programs on the left, topics on the right. Link thickness = topic weight for that program."
                        : dominanceView === "treemap"
                          ? "Click a topic in the treemap to drill into subtopics."
                          : dominanceLevel === "topic"
                            ? "Node size reflects chunk mentions per main topic."
                            : "Node size reflects chunk mentions per subtopic."}
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={dominanceView}
                    onChange={(_, value: DominanceView | null) => {
                      if (value) setDominanceView(value);
                    }}
                  >
                    <ToggleButton value="treemap">
                      <GridViewIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Treemap
                    </ToggleButton>
                    <ToggleButton value="network">
                      <AccountTreeIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Network
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                {scopeView === "compare" && byProgramLoading && (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                )}

                {scopeView === "compare" && !byProgramLoading && byProgram && (
                  <>
                    {dominanceView === "treemap" && expandedProgram && (
                      <Stack spacing={1.5}>
                        <Button
                          size="small"
                          onClick={() => setExpandedProgramId(undefined)}
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Back to all programs
                        </Button>
                        <TopicTreemap
                          hierarchy={expandedProgramHierarchy}
                          colorBySlug={colorBySlug}
                          height={560}
                          onSubtopicClick={setSubtopicSelection}
                          selectedSubtopicSlug={subtopicSelection?.subtopicSlug ?? null}
                          onDrillTopicChange={() => setSubtopicSelection(null)}
                        />
                      </Stack>
                    )}
                    {dominanceView === "treemap" && !expandedProgram && (
                      <ProgramTreemapGrid
                        programs={byProgram.programs}
                        colorBySlug={colorBySlug}
                        metric={metric}
                        legendTopics={summary.topics.map((topic) => ({
                          slug: topic.slug,
                          display: topic.display,
                        }))}
                        onExpandProgram={(programId) => setExpandedProgramId(programId)}
                      />
                    )}
                    {dominanceView === "network" && (
                      <ProgramTopicNetworkGraph
                        programs={byProgram.programs}
                        colorBySlug={colorBySlug}
                        metric={metric}
                        height={520}
                      />
                    )}
                  </>
                )}

                {scopeView === "aggregate" && topicHierarchy.length > 0 && (
                  <>
                    {dominanceView === "treemap" ? (
                      <TopicTreemap
                        hierarchy={topicHierarchy}
                        colorBySlug={colorBySlug}
                        onSubtopicClick={setSubtopicSelection}
                        selectedSubtopicSlug={subtopicSelection?.subtopicSlug ?? null}
                        onDrillTopicChange={() => setSubtopicSelection(null)}
                      />
                    ) : (
                      <TopicNetworkGraph hierarchy={topicHierarchy} level={dominanceLevel} />
                    )}
                  </>
                )}
              </Paper>
            )}

            <TopicTable topics={summary.topics} />
            <TopEpisodesSection topics={summary.topics} />

            <SubtopicChunksPanel
              selection={subtopicSelection}
              chunks={subtopicChunksData?.chunks ?? []}
              loading={subtopicChunksLoading}
              onClose={() => setSubtopicSelection(null)}
            />
          </Stack>
        )}

        {pageTab === "by-program" && (
          <Stack spacing={2}>
            {byProgramLoading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            )}
            {byProgram && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Program</TableCell>
                      <TableCell align="right">Episodes</TableCell>
                      <TableCell align="right">Total views</TableCell>
                      <TableCell>Top topics</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Total mentions per 1,000 views for this program">
                          <span>Mentions / 1k views</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byProgram.programs.map((program) => (
                      <TableRow
                        key={program.programId ?? "unassigned"}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleProgramRowClick(program)}
                      >
                        <TableCell>{program.programName}</TableCell>
                        <TableCell align="right">
                          {program.metrics.episodeCount}
                          {program.metrics.episodesWithTaxonomy > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {program.metrics.episodesWithTaxonomy} w/ taxonomy
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatViews(program.metrics.totalViews)}
                        </TableCell>
                        <TableCell>
                          <TopTopicsChips topics={program.topics} />
                        </TableCell>
                        <TableCell align="right">
                          {formatRate(programMentionsPer1k(program))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default PodcastTaxonomyTopics;
